import { broker } from '#~/agent/broker.js';
import { fetchAgentsForRoom } from '#~/api/agent/index.js';
import { getData } from '#~/api/storage/index.js';
import { useAuth0 } from '#~/auth/use-auth0.js';
import { config } from '#~/config/index.js';
import { isVideoDevice } from '#~/device/utils.js';
import { logger } from '#~/logger/logger.js';
// W12 (Track 4a) — observability for the diagnostic-path billing-info
// endpoint. Per logger/observability.ts the body is redacted via W3 at
// the sink before reaching console/Sentry.
import { logApiResponse } from '#~/logger/observability.js';
import { nats } from '#~/nats/index.js';
import { hide, post } from '#~/notifications/utils.js';
import { pbToJsDevice } from '#~/protobuf/device.js';
import type { PbState } from '#~/protobuf/types.js';
import { TransientPrefix } from '@studio/models/device';
import type { Room } from '@studio/models/room';
import { ConnectionMode, State } from '@tryiris-ai/common.protojs/agent/v1/agent';
import {
  BillingRegistryGetUsageRequest,
  BillingRegistryGetUsageResponse
} from '@tryiris-ai/common.protojs/api/v1/billing_registry';
import { BillingRegistryGetUsage } from '@tryiris-ai/mailroom-helper/billing';
import { toIdFromHex } from '../utils.js';
import { useSyncExternalStore } from 'react';
import {
  beginTrialCameraRead,
  finishTrialCameraRead,
  isCurrentTrialCameraRead,
  resetTrialCameraAccess,
  setTrialCameraRefresher
} from './camera-access.js';

let hasShownCloudUsageWarning = false;
let hasShownCloudUsageBlock = false;

// Non-prod billing seams. Two ways to seed a Pro plan without a cloud fetch
// so a Free ITG studio can add more than one camera and exercise
// auto-tracking:
//   - IRIS_E2E_TEST=1 + IRIS_E2E_BILLING_LIMITS=1|<json>  (HIL / Playwright)
//   - IRIS_ITG_ASSUME_PRO=1                                (a human on Iris-itg)
//
// Both are gated on the BAKED channel via `config.env`, which the Electron
// main process forwards from its build-time IRIS_APP_ENV over the
// get-env-config IPC. They are NOT gated on window.electron.process.env,
// because the preload exposes a copy of the live OS environment: a prod
// binary launched with IRIS_APP_ENV=itg exported would otherwise pass. CI
// e2e builds carry no .env.production, so their channel is the schema
// default `itg` and the seams keep working there.
// Per rule: assess-cost-and-policy-exposure-on-gating-changes
// (ai.knowledge/global): on a prod build neither flag can grant limits, so
// no metered or flat-fee exposure is added; the seams change nothing about
// when a paying customer is treated as entitled.
const E2E_DEFAULT_LIMITS = {
  hasAutoTracking: true,
  maxSources: 100,
  maxCollaborators: 100,
  includedCloudUsage: 9999,
  cloudUsageOverageAllowed: true
};

type ElectronEnv = Record<string, string | undefined> | undefined;

// The preload's `process.env` mirror. Undefined in the web build and in tests
// that do not install it.
function readElectronEnv(): ElectronEnv {
  return (
    globalThis as unknown as {
      electron?: { process?: { env?: Record<string, string | undefined> } };
    }
  ).electron?.process?.env;
}

// Read lazily, only after a seam flag matched: `config` is a Proxy whose first
// property access parses the whole env, and limits.tsx is imported by many
// modules whose tests never install a config mock.
function isNonProdChannel(): boolean {
  return config.env !== 'prod';
}

// IRIS_ITG_ASSUME_PRO=1 seeds a permissive plan for a human running the
// integration app against a Free studio. No-op on a prod build regardless of
// what the OS environment says. Carried from autotracking's uncommitted
// overlay and #1506.
function readItgAssumeProOverride(): BillingRegistryGetUsageResponse | null {
  const flag = readElectronEnv()?.IRIS_ITG_ASSUME_PRO;
  if (flag !== '1' && flag !== 'true') {
    return null;
  }
  if (!isNonProdChannel()) {
    return null;
  }
  return BillingRegistryGetUsageResponse.fromPartial({
    limits: { ...E2E_DEFAULT_LIMITS },
    usage: 0
  });
}

function readE2EBillingOverride(): BillingRegistryGetUsageResponse | null {
  const env = readElectronEnv();

  if (!env || env.IRIS_E2E_TEST !== '1') {
    return null;
  }
  if (!isNonProdChannel()) {
    return null;
  }

  const raw = env.IRIS_E2E_BILLING_LIMITS;
  if (!raw) {
    return null;
  }

  let limits = { ...E2E_DEFAULT_LIMITS };
  if (raw !== '1' && raw !== 'true') {
    try {
      const parsed = JSON.parse(raw);
      if (parsed && typeof parsed === 'object' && !Array.isArray(parsed)) {
        limits = { ...limits, ...parsed };
      } else {
        logger.warning(
          `[billing] IRIS_E2E_BILLING_LIMITS must be "1" or a JSON object; got: ${raw}`
        );
      }
    } catch (err) {
      logger.warning('[billing] Failed to parse IRIS_E2E_BILLING_LIMITS', err);
    }
  }

  return BillingRegistryGetUsageResponse.fromPartial({ limits, usage: 0 });
}

export let forceRefreshBilling: (
  expectedRoomID?: string,
  isAllowed?: () => boolean
) => Promise<void> = async () => {};

export let currentLimits: BillingRegistryGetUsageResponse | null = null;

// Per ENG-4165/ENG-4187 — billing limits are tri-state: "unknown" (still
// loading) and "error" (fetch failed) must be distinguishable from "loaded".
// A null/loading/errored response previously collapsed to maxSources ?? 0,
// i.e. "0 sources allowed -> limit reached", which spuriously gated paying
// studios. Gates must enforce ONLY when loaded.
export type BillingLimitsStatus = 'unknown' | 'loaded' | 'error';

export interface BillingLimitsState {
  status: BillingLimitsStatus;
  // Populated only when status === 'loaded'.
  limits?: BillingRegistryGetUsageResponse['limits'];
  usage?: number;
}

// Tracks whether currentLimits reflects a clean response, a failed read, or
// nothing yet. Reset to 'unknown' on room change (see the room setter below)
// so a studio switch shows the loading state, not the previous studio's
// stale numbers.
let limitsStatus: BillingLimitsStatus = 'unknown';
let billingLimitsSnapshot: BillingLimitsState = { status: 'unknown' };
const billingStateListeners = new Set<() => void>();

// Per rule: settle-every-terminal-branch (ai.knowledge/global) — wake React on every result.
const publishBillingLimitsState = (): void => {
  billingLimitsSnapshot = getBillingLimitsState();
  for (const listener of billingStateListeners) listener();
};

const subscribeBillingLimits = (listener: () => void): (() => void) => {
  billingStateListeners.add(listener);
  return () => billingStateListeners.delete(listener);
};

// The snapshot stays referentially stable between billing reads for React's store contract.
export const useBillingLimitsState = (): BillingLimitsState =>
  useSyncExternalStore(subscribeBillingLimits, () => billingLimitsSnapshot);

/** Fresh, Studio-scoped evidence for notice display; it never enforces access. */
export interface TrialNoticeBillingRead {
  readonly roomID: string | null;
  readonly status: BillingLimitsStatus;
  readonly response: BillingRegistryGetUsageResponse | null;
}

let noticeReadPending = false;
const noticeBillingListeners = new Set<(read: TrialNoticeBillingRead) => void>();

export const getTrialNoticeBillingRead = (): TrialNoticeBillingRead => ({
  roomID,
  status: noticeReadPending ? 'unknown' : limitsStatus,
  response: !noticeReadPending && limitsStatus === 'loaded' ? currentLimits : null
});

const notifyNoticeBillingListeners = (): void => {
  const read = getTrialNoticeBillingRead();
  for (const listener of noticeBillingListeners) {
    // A presentation subscriber cannot interrupt camera/cloud enforcement or
    // another subscriber. Per settle-every-terminal-branch (global rules).
    try {
      listener(read);
    } catch (error) {
      logger.error('Trial notice observer failed', error);
    }
  }
};

export const subscribeTrialNoticeBilling = (
  listener: (read: TrialNoticeBillingRead) => void
): (() => void) => {
  noticeBillingListeners.add(listener);
  listener(getTrialNoticeBillingRead());
  return () => {
    noticeBillingListeners.delete(listener);
  };
};

const safeLimitListeners: (() => void)[] = [];

// Wake + clear every safeLimits() waiter. This MUST run on EVERY terminal
// outcome of a load — success, structured error, AND transport throw — not only
// the success path. If an error path settles `limitsStatus` to 'error' without
// flushing here, any in-flight safeLimits() Promise never resolves and its
// caller hangs forever. Per ENG-4165/ENG-4187 (Copilot PR review).
const notifySafeLimitListeners = () => {
  publishBillingLimitsState();
  if (safeLimitListeners.length) {
    for (const listener of safeLimitListeners) {
      listener();
    }

    safeLimitListeners.length = 0;
  }
  noticeReadPending = false;
  notifyNoticeBillingListeners();
};

export const safeLimits = async () => {
  // Only block while limits are genuinely still loading ('unknown'). Once the
  // load has settled — 'loaded' OR 'error' — return immediately: an errored read
  // must not leave callers blocked, and returning `currentLimits` (null on a
  // first-load failure) keeps the gates permissive and matches
  // getBillingLimitsState(). Per ENG-4165/ENG-4187 (Copilot PR review — error
  // paths previously left safeLimits() callers hung).
  if (!currentLimits && limitsStatus === 'unknown') {
    const p = new Promise<void>((resolve) => {
      // The previous `() => resolve` pushed the resolver without ever calling
      // it, so the Promise never settled. Push a thunk that INVOKES resolve.
      safeLimitListeners.push(() => resolve());
    });

    await p;
  }

  return currentLimits;
};

let bilingInterval: NodeJS.Timeout | null = null;
let roomID: string | null = null;
let lastResp: BillingRegistryGetUsageResponse | null = null;
// In-flight initial-load promise for the current room. Per ENG-4165/ENG-4187
// (codex adversarial review #3): roomID MUST be assigned before load() runs
// because load() reads the module-global roomID. That meant a concurrent
// same-room caller arriving mid-load short-circuited to the just-cleared
// `null` limits — a transient permissive window where an over-limit room
// (e.g. via select-studio's canAddSourceToRoom) could slip the gate. Same-room
// callers now await this promise, so they observe the resolved limits and the
// gate enforces the real value.
let inflightLoad: Promise<void> | null = null;

export const useBillingLimitMonitor = () => {
  const auth = useAuth0();

  const getToken = async () => {
    return await auth.getAccessTokenSilently();
  };

  const load = async (isAllowed: () => boolean = () => true) => {
    if (!isAllowed() || !roomID) {
      return;
    }
    const requestedRoomID = roomID;
    // Fence every awaited result, including failures, so an old Studio or a
    // previous lifetime cannot reinstall obsolete limits or camera locks.
    const cameraRead = beginTrialCameraRead(requestedRoomID);
    // Payment observers also fence the account across token and network waits.
    const isCurrent = () => isAllowed() && isCurrentTrialCameraRead(cameraRead);
    // Same-Studio polling keeps inherited access limits, but stale Free is not
    // affirmative display evidence. Per absent-data-gates-permissively (global).
    noticeReadPending = true;
    notifyNoticeBillingListeners();

    // Either non-prod seam short-circuits the cloud fetch (no token, no NATS)
    // and seeds a loaded plan; prod builds fall through to the real fetch.
    const e2eOverride = readE2EBillingOverride() ?? readItgAssumeProOverride();
    if (e2eOverride) {
      lastResp = e2eOverride;
      currentLimits = e2eOverride;
      limitsStatus = 'loaded';
      finishTrialCameraRead(cameraRead, e2eOverride, 'loaded');
      // @ts-ignore
      window.limits = e2eOverride;
      // Mirror the clean-response arm below: the enforcement pass is what
      // UNLOCKS cloud. E2E_DEFAULT_LIMITS sets cloudUsageOverageAllowed:true,
      // the first arm of assesCloudUsageLimits(), so this resolves to
      // allowCloudUsage() — the studio's only authority for mutating every
      // agent to CONNECTION_MODE_CLOUD_AND_LOCAL + billingLocked:false. Without
      // it an embedded camera never leaves its boot connectionMode and never
      // publishes cloud video (e2e-hil bandwidth-governor: `allowing cloud
      // usage` present on green main, absent on every run carrying this seam).
      // blockCloudUsage() is unreachable under the defaults; a caller merging
      // JSON with cloudUsageOverageAllowed:false and usage over the included
      // limit WOULD force local — that is a property of the limits, not of
      // this call. Per rule: stay-in-scope (ai.knowledge/global).
      assesCloudUsageLimits();
      notifySafeLimitListeners();
      return;
    }

    // ENG-4521: the token fetch previously sat OUTSIDE the try below, so on the
    // web a missing refresh token made getAccessTokenSilently reject here
    // uncaught — and because the 45s interval never consumed load()'s promise,
    // posthog-js autocapture filed it as a crash every 45s. Contain the token
    // fetch in its own try/catch, mirroring the billing-read catch below. The
    // Awaited<ReturnType> keeps the inferred token type without a type assertion.
    let token: Awaited<ReturnType<typeof getToken>>;
    try {
      token = await getToken();
    } catch (error) {
      if (!isCurrent()) return;
      // ENG-4521: contain a token-unavailable rejection exactly like the billing
      // read failure below — mark 'error', clear limits, flush safeLimits()
      // waiters, and RETURN instead of rethrowing (the interval and the
      // fire-and-forget forceRefreshBilling caller have no catch, so a rethrow
      // becomes an unhandled rejection).
      //
      // Deliberately does NOT stop the 45s poll. getAccessTokenSilently rejects
      // on transient conditions too (network blip, timeout), which are
      // indistinguishable here from a permanently missing refresh token.
      // Stopping would leave limitsStatus pinned at 'error' — and the gates
      // permissive — for the life of the tab, since the room setter only
      // re-arms on an actual studio change. That trades a crash stream for a
      // silent plan-enforcement bypass. Containment alone removes the
      // $exception; letting the poll run means the next tick recovers on its
      // own once the token is available again.
      limitsStatus = 'error';
      logger.error('Billing limits token fetch failed', error);
      currentLimits = null;
      finishTrialCameraRead(cameraRead, null, 'error');
      notifySafeLimitListeners();
      return;
    }
    if (!isCurrent()) return;

    // W12 — observability: time the billing-info diagnostic call so the
    // emitted line includes elapsed_ms. The log is best-effort and
    // wrapped in try/catch inside the helper, so a failure here cannot
    // cause a render-time exception (W12 hard constraint).
    const startedAt = Date.now();
    // ENG-4201: BillingRegistryGetUsage now constructs with staleOnError:false,
    // so a failed billing read REJECTS instead of serving a stale plan. The call
    // stays TWO-arg (no force/CacheOptions 3rd arg): the pinned
    // mailroom-helper@3.0.25 request() has no CacheOptions param, so a 3rd arg is
    // a TS2353 — and freshness already comes from cacheTTL:0 at the endpoint.
    // Per rule: reference-rules-in-comments (ai.knowledge/global).
    try {
      const response = await BillingRegistryGetUsage.request(
        nats.cloudConnection(),
        BillingRegistryGetUsageRequest.fromPartial({
          roomID: toIdFromHex(requestedRoomID)!,
          token
        })
      );
      if (!isCurrent()) return;
      lastResp = response;
    } catch (error) {
      if (!isCurrent()) return;
      // Per ENG-4165/ENG-4187 — a transient transport failure marks the
      // 'error' state (gates stay permissive, but distinguishable from
      // 'unknown') instead of leaving stale 'loaded' numbers in place.
      limitsStatus = 'error';
      logger.error('Billing limits fetch failed', error);
      // ENG-4201: with staleOnError:false the request REJECTS on failure. CLEAR
      // currentLimits so consumers degrade to an unknown/blocked plan rather than
      // seeing the last plan as current — per knowledge
      // cache-fallback-defeats-recovery (ai.knowledge/global), never serve a
      // stale plan.
      currentLimits = null;
      finishTrialCameraRead(cameraRead, null, 'error');
      // Wake any safeLimits() waiters on the error path — a failed first load
      // would otherwise leave them blocked forever. This MUST run on the
      // transport-throw path too. Per ENG-4165/ENG-4187 (Copilot PR review).
      notifySafeLimitListeners();
      // ENG-4201: both call paths — the 45s interval below and the
      // fire-and-forget forceRefreshBilling caller in billing.tsx — have no
      // catch, so rethrowing would surface an UNHANDLED promise rejection during
      // a backend outage. RETURN to contain the rejection instead of rethrowing;
      // the error is already captured in limitsStatus ('error') and the gates
      // handle it.
      return;
    }

    // 'billing-info' is on the diagnostic short list; the helper logs
    // the full (redacted) body so support can grep
    // `api.response endpoint=billing-info` to see plan/usage at the
    // moment of a customer incident.
    logApiResponse('billing-info', 200, Date.now() - startedAt, lastResp);

    // @ts-ignore
    window.limits = lastResp;

    if (lastResp !== null && lastResp.error === undefined && lastResp.limits !== undefined) {
      currentLimits = lastResp;
      finishTrialCameraRead(cameraRead, lastResp, 'loaded');
      // Per ENG-4165/ENG-4187 — only a clean response flips us to 'loaded',
      // the one state where the gates enforce the real limit.
      limitsStatus = 'loaded';

      // Assess cloud-usage enforcement ONLY on a clean response. On an errored
      // response lastResp.limits is undefined, so assesCloudUsageLimits() reads
      // includedCloudUsage/usage as 0 and hits `0 >= 0` -> blockCloudUsage(),
      // spuriously disabling cloud mode — the same absent-limits-as-enforcement
      // class as the source-limit bug. Previously this ran before the error
      // check, so any errored read disabled cloud usage. Per ENG-4165/ENG-4187
      // (sibling audit).
      assesCloudUsageLimits();

      notifySafeLimitListeners();
    } else {
      // Per ENG-4165/ENG-4187 — a structured error (e.g. PLAN_UNKNOWN) marks
      // 'error' so gates stay permissive without claiming the limit is known.
      limitsStatus = 'error';
      currentLimits = null;
      finishTrialCameraRead(cameraRead, null, 'error');
      // Settle safeLimits() waiters on the structured-error path too, so a
      // PLAN_UNKNOWN / errored response can't leave callers hung. Per
      // ENG-4165/ENG-4187 (Copilot PR review).
      notifySafeLimitListeners();
    }
  };

  const forceRefresh = async (expectedRoomID?: string, isAllowed?: () => boolean) => {
    // A payment for another Studio must not mutate the active Studio's limits.
    // load() already fences token/network awaits by Studio and generation.
    if (expectedRoomID !== undefined && expectedRoomID !== roomID) return;
    // ENG-4201: freshness comes from cacheTTL:0 on BillingRegistryGetUsage, NOT a
    // force flag. Do NOT add a force/CacheOptions arg to load()/request() here —
    // the pinned mailroom-helper@3.0.25 request() has no CacheOptions param, so it
    // would be a TS2353. Per rule: reference-rules-in-comments (ai.knowledge/global).
    await load(isAllowed);
  };

  forceRefreshBilling = forceRefresh;
  setTrialCameraRefresher(async (requestedRoom) => {
    if (roomID === requestedRoom) await load();
  });

  return async (nextRoomID: string) => {
    if (roomID === nextRoomID) {
      // Same room: if the initial load is still in flight, await it so the
      // caller observes the resolved limits instead of the transient
      // cleared-null window. Tolerate a load rejection here — the failure is
      // already captured in limitsStatus ('error') and the gates handle it,
      // and a same-room caller never threw under the old short-circuit. Per
      // ENG-4165/ENG-4187 (codex adversarial review #3).
      if (inflightLoad) {
        await inflightLoad.catch(() => {});
      }
      return currentLimits;
    }

    // Per ENG-4165/ENG-4187 — currentLimits/lastResp are module-globals that
    // otherwise leak across studio switches. Clear them (and reset to
    // 'unknown') on room change so the next studio shows the loading state,
    // not the previous studio's stale numbers (which could spuriously read
    // as "limit reached").
    currentLimits = null;
    lastResp = null;
    limitsStatus = 'unknown';
    resetTrialCameraAccess(nextRoomID);
    publishBillingLimitsState();

    if (bilingInterval) {
      clearInterval(bilingInterval);
    }

    bilingInterval = setInterval(
      () => {
        // ENG-4521: setInterval discards the promise its async callback returns,
        // so a load() rejection would be unhandled by construction (posthog-js
        // autocapture then files it as a crash). load() contains its own errors
        // today, but the interval must own any rejection so a future load()
        // refactor cannot reintroduce the unhandled path.
        void load().catch((error) => {
          logger.error('Billing limits poll failed', error);
        });
      },
      1000 * 45 * 1
    );

    // roomID MUST be set before load() — load() reads the module-global
    // roomID. Track the load as inflightLoad so a concurrent same-room caller
    // (e.g. a second select-studio render) awaits this resolution instead of
    // seeing the cleared null. Per ENG-4165/ENG-4187 (codex review #3).
    roomID = nextRoomID;
    const ownedLoad = load().finally(() => {
      // An old Studio can settle after a new one has begun loading. Only
      // its owner may clear the promise that same-Studio callers await.
      if (inflightLoad === ownedLoad) inflightLoad = null;
    });
    inflightLoad = ownedLoad;
    await ownedLoad;

    return currentLimits;
  };
};

// Tri-state accessor for the billing limits read. Callers gate permissively
// while unknown/error and enforce only when loaded. Per ENG-4165/ENG-4187 —
// this replaces the `currentLimits?.limits?.maxSources ?? 0` deny-on-absence
// that treated a loading/errored response as "0 sources allowed".
export const getBillingLimitsState = (): BillingLimitsState => {
  if (limitsStatus === 'loaded' && currentLimits) {
    return { status: 'loaded', limits: currentLimits.limits, usage: currentLimits.usage };
  }

  if (limitsStatus === 'error') {
    return { status: 'error' };
  }

  return { status: 'unknown' };
};

export const limitCanAddCamera = () => {
  const state = getBillingLimitsState();

  // Per ENG-4165/ENG-4187 + rule stay-in-scope (ai.knowledge/workflow) —
  // permissive while the limit is unknown/error, so a loading or failed
  // billing read never spuriously blocks adding a source. Enforcement applies
  // ONLY once limits are loaded.
  if (state.status !== 'loaded') {
    return true;
  }

  // Loaded: enforce the real limit. A known zero still denies.
  return countMirroredVideoSources() < (state.limits?.maxSources ?? 0);
};

// Video sources across every agent this renderer currently mirrors.
//
// Embedded agents are counted deliberately. Per ENG-3023 (#945) — excluding
// them re-opens the bypass this count exists to close (footer.tsx's `!embedded`
// filter is only for the manual-RTSP affordance, which embedded cameras cannot
// host).
//
// Extracted so the render-time gate and the commit-time gate count the same
// way; the difference between them is what ELSE they consult, not the tally.
const countMirroredVideoSources = (): number => {
  let totalDeviceCount = 0;

  for (const agent of broker) {
    const deviceCount = Object.entries(agent.state.control?.device ?? {}).filter(
      ([id, _]) =>
        !id.startsWith(TransientPrefix) &&
        isVideoDevice(pbToJsDevice(agent.id, id, agent.state as PbState, agent?.embedded ?? false))
    ).length;

    totalDeviceCount += deviceCount;
  }

  return totalDeviceCount;
};

/**
 * Slots remaining before the billing cap is hit.
 * Mirrors limitCanAddCamera's broker iteration — same source-of-truth, returns a count
 * instead of a boolean so the bulk-add UI can cap checkbox selection precisely.
 *
 * Returns Math.max(0, maxSources - totalDeviceCount) so callers never see negatives
 * when the org is already over-limit (e.g. plan downgrade).
 *
 * Per rule: code-comments-required (ai.knowledge/global) — explain why, not what.
 */
export const remainingCameraSlots = (): number => {
  const agents = broker;

  let totalDeviceCount = 0;

  for (const agent of agents) {
    const deviceCount = Object.entries(agent.state.control?.device ?? {}).filter(
      ([id, _]) =>
        !id.startsWith(TransientPrefix) &&
        isVideoDevice(pbToJsDevice(agent.id, id, agent.state as PbState, agent?.embedded ?? false))
    ).length;

    totalDeviceCount += deviceCount;
  }

  // No plan or zero-limit → treat as fully consumed
  const maxSources = currentLimits?.limits?.maxSources ?? 0;
  return Math.max(0, maxSources - totalDeviceCount);
};

export const getSourceCountForRoom = async (roomId: Room['id'], token: string): Promise<number> => {
  const agents = await fetchAgentsForRoom(roomId, token);

  const results = await Promise.all(
    Object.values(agents).map(async (agent) => {
      try {
        const bytes = await getData(toIdFromHex(agent.id)!, token);
        if (!bytes.length) return 0;

        const state = State.decode(bytes) as PbState;
        return Object.entries(state.control?.device ?? {}).filter(
          ([id]) =>
            !id.startsWith(TransientPrefix) &&
            isVideoDevice(pbToJsDevice(agent.id, id, state, agent.embedded ?? false))
        ).length;
      } catch (error) {
        logger.error('Failed to load state for agent when counting room sources', agent.id, error);
        return 0;
      }
    })
  );

  return results.reduce((sum, count) => sum + count, 0);
};

/**
 * The gate a COMMIT should ask, as opposed to the one a render can ask.
 *
 * limitCanAddCamera() counts the agents this renderer currently mirrors. That
 * mirror is rebuilt from scratch on every page load and refills asynchronously,
 * so for the first seconds of a session it reports fewer sources than the room
 * holds — and the onboarding wizard's add runs inside exactly that gap, because
 * linking an Iris-enabled camera reloads the renderer. A studio at 1 of 1
 * sources counted 0 and admitted a second one (ENG-4649).
 *
 * So take the higher of the two counts. The mirror sees a device the instant
 * this session adds it, before any server write has landed; the room read sees
 * every device this session has not heard about yet. Neither alone is the
 * source count, and believing the smaller one is what lets a source through.
 *
 * Tri-state discipline is unchanged (ENG-4165/ENG-4187): a loading or failed
 * billing read still gates permissively, because refusing a paying customer on
 * a slow read is the worse failure. Only a loaded limit enforces.
 *
 * Deliberately NOT a change to canAddSourceToRoom below, which it otherwise
 * resembles. That one answers "may this room take a source" for select-studio,
 * where the mirror holds a DIFFERENT room's agents and folding it in would
 * count the wrong studio's cameras. This one answers "may THIS session add one
 * here", where the mirror and the room are the same room.
 *
 * Per rules: think-in-systems, reference-rules-in-comments (ai.knowledge/global).
 */
export const canAddCameraToRoom = async (roomId: Room['id'], token: string): Promise<boolean> => {
  const state = getBillingLimitsState();

  if (state.status !== 'loaded') {
    return true;
  }

  const maxSources = state.limits?.maxSources ?? 0;
  const localCount = countMirroredVideoSources();

  // The cheap count already denies: no reason to pay for the network read.
  if (localCount >= maxSources) {
    return false;
  }

  let roomCount = 0;
  try {
    roomCount = await getSourceCountForRoom(roomId, token);
  } catch {
    // The room read failing is not evidence of room contents. Fall back to the
    // mirror, which has already said this add is under the limit.
    return true;
  }

  return Math.max(localCount, roomCount) < maxSources;
};

export const canAddSourceToRoom = async (roomId: Room['id'], token: string): Promise<boolean> => {
  const state = getBillingLimitsState();

  // Per ENG-4165/ENG-4187 — never deny a paying customer on a loading/failed
  // billing read; gate permissively until limits load. Short-circuit before
  // the (network) source count so we don't pay for it while unknown. This is
  // the gate select-studio.tsx relies on, so that surface inherits the fix.
  if (state.status !== 'loaded') {
    return true;
  }

  const count = await getSourceCountForRoom(roomId, token);

  // Loaded: enforce the real limit. A known zero still denies.
  return count < (state.limits?.maxSources ?? 0);
};

export const limitCanAddCollaborator = (currentCount: number) => {
  const state = getBillingLimitsState();

  // Per ENG-4165/ENG-4187 — permissive while unknown/error; enforce only when
  // loaded so a loading billing read never blocks adding a collaborator.
  if (state.status !== 'loaded') {
    return true;
  }

  return currentCount < (state.limits?.maxCollaborators ?? 0);
};

const blockCloudUsage = async () => {
  if (!cloudUsageAllowed && new Date().getTime() - cloudUsageLastDisallowedAt < 5000) {
    return;
  }

  cloudUsageAllowed = false;
  cloudUsageLastDisallowedAt = new Date().getTime();

  console.log('blocking cloud usage');

  const includedLimit = lastResp?.limits?.includedCloudUsage ?? 0;

  if (!hasShownCloudUsageBlock) {
    hide('cloud-usage-warning');
    hide('cloud-usage-error');
    post(
      {
        actionable: true,
        id: 'cloud-usage-error',
        title: `Cloud usage is disabled`,
        message: (
          <span>
            You have consumed {Math.round(includedLimit * 100) / 100}GB of your included cloud usage
            limit of {Math.round(includedLimit * 100) / 100}GB.
            <br />
            <br />
            Local mode will continue to function.
            <br />
            <br />
            {/* <Button
            size='sm'
            onClick={() => {
              window.location.href = window.location.href + '/billing/web/upgrade';
            }}
          >
            Upgrade Now
          </Button> */}
          </span>
        ),
        timestamp: new Date(),
        severity: 'error'
      },
      {
        autoClose: 10_000
      }
    );

    hasShownCloudUsageBlock = true;
  }

  for (const proxy of broker) {
    proxy.mutate(
      {
        control: {
          encoder: {
            connectionMode: ConnectionMode.CONNECTION_MODE_LOCAL,
            billingLocked: true
          }
        }
      },
      {},
      true
    );
  }
};

const allowCloudUsage = async (autoRepeat = true) => {
  if (cloudUsageAllowed && new Date().getTime() - cloudUsageLastAllowedAt < 5000) {
    return;
  }

  cloudUsageAllowed = true;
  cloudUsageLastAllowedAt = new Date().getTime();

  console.log('allowing cloud usage');

  let count = 0;
  for (const proxy of broker) {
    count++;

    if (proxy.state.control?.encoder?.billingLocked === false) {
      continue;
    }

    if (
      proxy.state.control?.encoder?.connectionMode !==
      ConnectionMode.CONNECTION_MODE_CLOUD_AND_LOCAL
    ) {
      autoRepeat = true;
    }

    proxy.mutate(
      {
        control: {
          encoder: {
            connectionMode: ConnectionMode.CONNECTION_MODE_CLOUD_AND_LOCAL,
            billingLocked: false
          }
        }
      },
      {},
      false
    );
  }

  if (count === 0 || autoRepeat) {
    setTimeout(() => allowCloudUsage(false), 1000);
  }
};

const assesCloudUsageLimits = async () => {
  // Per absent-data-gates-permissively: a failed trial read is not an exhausted zero allowance.
  if (lastResp !== null && lastResp.trialEnd > 0 && lastResp.billingStateKnown !== true) return;

  const overageAllowed = lastResp?.limits?.cloudUsageOverageAllowed;

  // If overages are allowed, we don't need to do anything.
  if (overageAllowed) {
    await allowCloudUsage();

    return;
  }

  const usage = lastResp?.usage ?? 0;
  const includedLimit = lastResp?.limits?.includedCloudUsage ?? 0;

  // if we are under 75% of the included limit, we don't need to do anything.
  if (usage < includedLimit * 0.75) {
    await allowCloudUsage();

    return;
  }

  // if we are over 75% of the included limit, but under the limit, we need to warn the user.
  if (usage < includedLimit) {
    if (!hasShownCloudUsageWarning) {
      hide('cloud-usage-warning');
      post(
        {
          id: 'cloud-usage-warning',
          title: `Cloud usage is nearing the limit`,
          message: (
            <span>
              You have consumed {Math.round(usage * 100) / 100}GB of your included cloud usage limit
              of {Math.round(includedLimit * 100) / 100}GB.
              <br />
              <br />
              Please upgrade your plan to avoid disruption.
              <br />
              <br />
              {/* <Button
              size='sm'
              onClick={() => {
                window.location.href = window.location.href + '/billing/web/upgrade';
              }}
            >
              Upgrade Now
            </Button> */}
            </span>
          ),
          timestamp: new Date(),
          severity: 'warning'
        },
        {
          autoClose: 10_000
        }
      );

      hasShownCloudUsageWarning = true;
    }

    await allowCloudUsage();

    return;
  }

  // if we are over the limit, we need to warn the user and disable the room.
  if (usage >= includedLimit) {
    await blockCloudUsage();
    return;
  }

  // otherwise, we can allow cloud usage.
  await allowCloudUsage();
};

export const limitCanUseCloudMode = () => {
  if (!currentLimits) {
    return false;
  }

  if (currentLimits.limits?.cloudUsageOverageAllowed) {
    return true;
  }

  if (currentLimits?.usage >= (currentLimits?.limits?.includedCloudUsage ?? 0)) {
    return false;
  }

  return true;
};

let cloudUsageAllowed = true;
let cloudUsageLastAllowedAt = 0;
let cloudUsageLastDisallowedAt = 0;
